import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { Teacher, User } from '../../../utils/firebase';
import { SUBJECTS, SubjectType, getSubjectId, getSubjectName } from '../../../utils/subjectUtils';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import DropdownButton from '../../../components/ui/DropdownButton';

const pxToRem = (value: number) => `${value / 16}rem`;

const SubjectSetting: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const teacher = currentUser as Teacher;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (teacher) {
      setName(teacher.name);
      if (teacher.subject) {
        const subjectName = getSubjectName(teacher.subject);
        setSubject(subjectName);
      }
    }
  }, [teacher]);

  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!teacher?.uid || !teacher?.schoolId) return;
      try {
        const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
        const teacherDoc = await getDoc(teacherRef);
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          if (data.subject) {
            setSubject(getSubjectName(data.subject));
          }
          if (data.name) {
            setName(data.name);
          }
        }
      } catch (err) {
        console.error('교사 데이터 로딩 중 오류:', err);
      }
    };
    fetchTeacherData();
  }, [teacher?.uid, teacher?.schoolId]);

  const subjectOptions = (Object.keys(SUBJECTS) as SubjectType[]).map(subjectName => ({
    value: subjectName,
    label: subjectName
  }));

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    if (!teacher?.uid || !teacher?.schoolId) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const subjectId = subject ? getSubjectId(subject as SubjectType) : '';
      const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);

      await updateDoc(teacherRef, {
        name: name.trim(),
        subject: subjectId,
        subjectId: subjectId,
        updatedAt: serverTimestamp()
      });

      setCurrentUser((prev: User | null) => {
        if (!prev || prev.role !== 'teacher') return prev;
        return {
          ...prev,
          name: name.trim(),
          subject: subjectId,
          subjectId: subjectId
        };
      });

      setSuccess('설정이 저장되었습니다.');
    } catch (err) {
      console.error('설정 저장 중 오류:', err);
      setError('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!teacher) {
    return null;
  }

  return (
    <div style={{
      width: '100%',
      minHeight: 'fit-content',
      display: 'flex',
      flexDirection: 'column',
      gap: pxToRem(32),
      fontFamily: 'Pretendard, sans-serif'
    }}>
      <div style={{
        alignSelf: 'stretch',
        color: 'var(--Color-text-normal, #161616)',
        fontSize: pxToRem(32),
        fontFamily: 'Pretendard, sans-serif',
        fontWeight: '600',
        lineHeight: pxToRem(48),
        wordWrap: 'break-word'
      }}>
        설정
      </div>

      {error && (
        <div style={{
          marginBottom: 16,
          padding: 12,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 8
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          marginBottom: 16,
          padding: 12,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: 8
        }}>
          {success}
        </div>
      )}

      <Card
        width="100%"
        padding={`${pxToRem(24)} ${pxToRem(28)}`}
        alignItems="flex-start"
        gap={pxToRem(24)}
      >
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(24)
        }}>
          <div style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(18),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '600',
            lineHeight: pxToRem(27),
            wordWrap: 'break-word'
          }}>
            교과 설정
          </div>
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(16)
          }}>
            <InputField
              value={name}
              onChange={setName}
              placeholder="이름을 입력하세요"
              width="100%"
              height={pxToRem(48)}
              size="large"
              type="text"
              label="이름"
            />
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(8)
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(14),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '500',
                lineHeight: pxToRem(21)
              }}>
                담당 교과
              </div>
              <DropdownButton
                options={subjectOptions}
                value={subject}
                onChange={(val) => setSubject(val)}
                placeholder="교과 선택"
                width="100%"
                height={pxToRem(48)}
                size="large"
              />
            </div>
          </div>
        </div>
      </Card>

      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: pxToRem(24)
      }}>
        <Button
          variant="submit"
          width={pxToRem(118)}
          height={pxToRem(48)}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? '저장 중...' : '저장'}
        </Button>
      </div>
    </div>
  );
};

export default SubjectSetting;
